import clsx from "clsx";
import * as React from "react";
import Button from "../../common/Button/Button";
import DropdownMenu from "../../common/DropdownMenu/DropdownMenu";
import Icon from "../../common/Icon/Icon";
import styles from "./InvestigationCard.module.scss";

export type InvestigationCardMode = "view" | "edit";

export type InvestigationCardMenuAction = {
  id: string;
  label: string;
  destructive?: boolean;
  disabled?: boolean;
  onSelect: () => void;
};

export interface InvestigationCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  title?: React.ReactNode;
  mode?: InvestigationCardMode;
  defaultMode?: InvestigationCardMode;
  selected?: boolean;
  palette?: boolean;
  editable?: boolean;
  movable?: boolean;
  resizable?: boolean;
  deletable?: boolean;
  addable?: boolean;
  front: React.ReactNode;
  back?: React.ReactNode;
  headerActions?: React.ReactNode;
  menuActions?: InvestigationCardMenuAction[];
  onModeChange?: (mode: InvestigationCardMode) => void;
  onMovePointerDown?: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onResizePointerDown?: (event: React.PointerEvent<HTMLElement>) => void;
  onDelete?: () => void;
  onAdd?: () => void;
}

const InvestigationCard = ({
  className,
  title,
  mode,
  defaultMode = "view",
  selected = false,
  palette = false,
  editable = true,
  movable = true,
  resizable = true,
  deletable = true,
  addable = false,
  front,
  back,
  headerActions,
  menuActions = [],
  onModeChange,
  onMovePointerDown,
  onResizePointerDown,
  onDelete,
  onAdd,
  ...props
}: InvestigationCardProps) => {
  const [internalMode, setInternalMode] = React.useState<InvestigationCardMode>(defaultMode);
  const currentMode = mode ?? internalMode;
  const canEdit = editable && Boolean(back);
  const setMode = (nextMode: InvestigationCardMode) => {
    if (mode === undefined) setInternalMode(nextMode);
    onModeChange?.(nextMode);
  };

  const hasMenu = canEdit || deletable || addable || menuActions.length > 0;

  return (
    <div
      className={clsx(styles.card, className)}
      data-selected={selected ? "true" : "false"}
      data-palette={palette ? "true" : "false"}
      {...props}
    >
      <div className={styles.header}>
        {movable ? (
          <Button
            icon
            size="sm"
            variant="ghost"
            label="Move card"
            className={clsx(styles.dragHandle, "investigation-card-drag-handle")}
            onPointerDown={onMovePointerDown}
          >
            <Icon name="drag" />
          </Button>
        ) : null}
        <div className={styles.title}>{title || "Investigation card"}</div>
        <div className={styles.controls}>
          {headerActions ? <div className={styles.headerActions}>{headerActions}</div> : null}
          {canEdit ? (
            <Button
              icon
              size="sm"
              variant="ghost"
              label={currentMode === "edit" ? "View card" : "Edit card"}
              onClick={() => setMode(currentMode === "edit" ? "view" : "edit")}
            >
              <Icon name={currentMode === "edit" ? "visibility" : "edit"} />
            </Button>
          ) : null}
          {hasMenu ? (
            <DropdownMenu>
              <DropdownMenu.Trigger asChild>
                <Button
                  icon
                  size="sm"
                  variant="ghost"
                  label="Card menu"
                >
                  <Icon name="show-more" />
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="end">
                {addable ? (
                  <DropdownMenu.Item onClick={onAdd}>
                    <DropdownMenu.Item.Icon>
                      <Icon name="add" />
                    </DropdownMenu.Item.Icon>
                    <DropdownMenu.Item.Label>Add to investigation</DropdownMenu.Item.Label>
                  </DropdownMenu.Item>
                ) : null}
                {canEdit ? (
                  <DropdownMenu.Item onClick={() => setMode(currentMode === "edit" ? "view" : "edit")}>
                    <DropdownMenu.Item.Icon>
                      <Icon name={currentMode === "edit" ? "visibility" : "edit"} />
                    </DropdownMenu.Item.Icon>
                    <DropdownMenu.Item.Label>
                      {currentMode === "edit" ? "View card" : "Edit card"}
                    </DropdownMenu.Item.Label>
                  </DropdownMenu.Item>
                ) : null}
                {menuActions.map((action) => (
                  <DropdownMenu.Item
                    key={action.id}
                    disabled={action.disabled}
                    color={action.destructive ? "destructive" : "default"}
                    onClick={action.onSelect}
                  >
                    <DropdownMenu.Item.Label>{action.label}</DropdownMenu.Item.Label>
                  </DropdownMenu.Item>
                ))}
                {deletable ? (
                  <DropdownMenu.Item
                    color="destructive"
                    onClick={onDelete}
                  >
                    <DropdownMenu.Item.Icon>
                      <Icon name="delete" />
                    </DropdownMenu.Item.Icon>
                    <DropdownMenu.Item.Label>Delete card</DropdownMenu.Item.Label>
                  </DropdownMenu.Item>
                ) : null}
              </DropdownMenu.Content>
            </DropdownMenu>
          ) : null}
        </div>
      </div>
      <div className={styles.content}>
        {currentMode === "edit" ? back || <div className={styles.empty}>No card settings.</div> : front}
        {resizable ? (
          <button
            type="button"
            className={styles.resizeHandle}
            aria-label="Resize card"
            onPointerDown={onResizePointerDown}
          >
            <Icon name="resize" />
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default InvestigationCard;
